<script lang="ts">
    import {onMount} from 'svelte';
    import {t} from '../lib/i18n';
    import {listUsers, listTimeSeries, listOpenAIModels, userApi, openaiModelsApi} from '../lib/api';
    import {currentAdminTab, addNotification} from '../stores/uiStore';
    import type {User, TimeSeriesMeta, OpenAIModel} from '../lib/types';

    // Import admin components
    import UserForm from '../components/admin/UserForm.svelte';
    import OpenAIModelForm from '../components/admin/OpenAIModelForm.svelte';
    import ConfirmDeleteDialog from '../components/admin/ConfirmDeleteDialog.svelte';

    // Component state
    let activeTab = 'users';
    let users: User[] = [];
    let timeseries: TimeSeriesMeta[] = [];
    let openAIModels: OpenAIModel[] = [];
    let loading = true;
    let error = '';

    // Modal state
    let showUserForm = false;
    let showModelForm = false;
    let showDeleteConfirm = false;
    let selectedUserId: number | string | null = null;
    let selectedModelId: number | string | null = null;
    let selectedItemId: number | string | null = null;
    let deleteItemType = '';
    let deleteItemName = '';
    let deleteInProgress = false;

    // Set current admin tab in store and update local state
    function setTab(tab: string) {
        activeTab = tab;
        $currentAdminTab = tab;
    }

    // Load data based on active tab
    async function loadData() {
        loading = true;
        error = '';

        try {
            if (activeTab === 'users') {
                users = await listUsers();
            } else if (activeTab === 'timeseries') {
                timeseries = await listTimeSeries();
            } else if (activeTab === 'openai-models') {
                openAIModels = await listOpenAIModels(false);
            }
        } catch (err) {
            error = err.message || t('Error loading data');
        } finally {
            loading = false;
        }
    }

    // Initialize component
    onMount(async () => {
        // Set active tab from store if available
        if ($currentAdminTab) {
            activeTab = $currentAdminTab;
        }

        await loadData();
    });

    // Watch for tab changes
    $: if (activeTab) {
        loadData();
    }

    // User form functions
    function openAddUserForm() {
        selectedUserId = null;
        showUserForm = true;
    }

    function openEditUserForm(userId: number) {
        selectedUserId = userId;
        showUserForm = true;
    }

    // OpenAI Model form functions
    function openAddModelForm() {
        selectedModelId = null;
        showModelForm = true;
    }

    function openEditModelForm(modelId: string) {
        selectedModelId = modelId;
        showModelForm = true;
    }

    // Delete confirmation functions
    function confirmDelete(type: string, id: string, name: string) {
        deleteItemType = type;
        selectedItemId = id;
        deleteItemName = name;
        showDeleteConfirm = true;
    }

    async function handleDelete() {
        deleteInProgress = true;

        try {
            if (deleteItemType === 'user' && selectedItemId !== null) {
                await userApi.delete(selectedItemId);
                addNotification('success', t('User deleted successfully'));
            } else if (deleteItemType === 'model' && selectedItemId !== null) {
                await openaiModelsApi.delete(selectedItemId);
                addNotification('success', t('Model deleted successfully'));
            }

            // Reload data and close dialog
            await loadData();
            showDeleteConfirm = false;
        } catch (err) {
            addNotification('error', err.message || t('Error deleting item'));
        } finally {
            deleteInProgress = false;
        }
    }
</script>

<div class="container-fluid mt-4">
    <h2>Администрирование</h2>
    <ul class="nav nav-tabs mb-3">
        <li class="nav-item">
            <button class="nav-link {activeTab === 'users' ? 'active' : ''}" on:click={() => setTab('users')}>
                {t('Users')}
            </button>
        </li>
        <li class="nav-item">
            <button class="nav-link {activeTab === 'timeseries' ? 'active' : ''}" on:click={() => setTab('timeseries')}>
                {t('Time Series')}
            </button>
        </li>
        <li class="nav-item">
            <button
                    class="nav-link {activeTab === 'forecasts' ? 'active' : ''}"
                    on:click={() => setTab('forecasts')}
            >
                {t('Forecasts')}
            </button>
        </li>
        <li class="nav-item">
            <button
                    class="nav-link {activeTab === 'ai-analyses' ? 'active' : ''}"
                    on:click={() => setTab('ai-analyses')}
            >
                {t('AI Analyses')}
            </button>
        </li>
        <li class="nav-item">
            <button
                    class="nav-link {activeTab === 'openai-models' ? 'active' : ''}"
                    on:click={() => setTab('openai-models')}
            >
                {t('OpenAI Models')}
            </button>
        </li>
    </ul>

    {#if loading}
        <div class="d-flex justify-content-center">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">{t('Loading...')}</span>
            </div>
        </div>
    {:else if error}
        <div class="alert alert-danger">
            {error}
        </div>
    {:else}
        <!-- User management -->
        {#if activeTab === 'users'}
            <div class="card">
                <div class="card-header d-flex justify-content-between align-items-center">
                    <h5>{t('User Management')}</h5>
                    <button class="btn btn-sm btn-primary" on:click={openAddUserForm}>
                        <i class="bi bi-plus-circle me-1"></i> {t('Add User')}
                    </button>
                </div>
                <div class="card-body">
                    <table class="table">
                        <thead>
                        <tr>
                            <th>ID</th>
                            <th>{t('Username')}</th>
                            <th>{t('Email')}</th>
                            <th>{t('Role')}</th>
                            <th>{t('Actions')}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {#if users.length === 0}
                            <tr>
                                <td colspan="5" class="text-center">{t('No users found')}</td>
                            </tr>
                        {:else}
                            {#each users as user}
                                <tr>
                                    <td>{user.id}</td>
                                    <td>{user.username}</td>
                                    <td>{user.email}</td>
                                    <td>{user.role || 'user'}</td>
                                    <td>
                                        <button
                                                class="btn btn-sm btn-outline-primary me-1"
                                                on:click={() => openEditUserForm(user.id)}
                                                title={t('Edit User')}
                                        >
                                            <i class="bi bi-pencil"></i>
                                        </button>
                                        <button
                                                class="btn btn-sm btn-outline-danger"
                                                on:click={() => confirmDelete('user', user.id, user.username)}
                                                title={t('Delete User')}
                                        >
                                            <i class="bi bi-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            {/each}
                        {/if}
                        </tbody>
                    </table>
                </div>
            </div>
        {/if}

        <!-- Time Series management -->
        {#if activeTab === 'timeseries'}
            <div class="card">
                <div class="card-header d-flex justify-content-between align-items-center">
                    <h5>{t('Time Series Management')}</h5>
                    <button class="btn btn-sm btn-primary">
                        <i class="bi bi-plus-circle me-1"></i> {t('Add Time Series')}
                    </button>
                </div>
                <div class="card-body">
                    <table class="table">
                        <thead>
                        <tr>
                            <th>ID</th>
                            <th>{t('Name')}</th>
                            <th>{t('User')}</th>
                            <th>{t('Data Points')}</th>
                            <th>{t('Created At')}</th>
                            <th>{t('Actions')}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {#if timeseries.length === 0}
                            <tr>
                                <td colspan="6" class="text-center">{t('No time series found')}</td>
                            </tr>
                        {:else}
                            {#each timeseries as series}
                                <tr>
                                    <td>{series.id}</td>
                                    <td>{series.name}</td>
                                    <td>{series.user_id}</td>
                                    <td>{series.data_points?.length || 0}</td>
                                    <td>{new Date(series.created_at).toLocaleString()}</td>
                                    <td>
                                        <button class="btn btn-sm btn-outline-primary me-1">
                                            <i class="bi bi-pencil"></i>
                                        </button>
                                        <button class="btn btn-sm btn-outline-secondary me-1">
                                            <i class="bi bi-bar-chart"></i>
                                        </button>
                                        <button class="btn btn-sm btn-outline-danger">
                                            <i class="bi bi-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            {/each}
                        {/if}
                        </tbody>
                    </table>
                </div>
            </div>
        {/if}

        <!-- Forecasts management -->
        {#if activeTab === 'forecasts'}
            <div class="card">
                <div class="card-header">
                    <h5>{t('Forecasts Management')}</h5>
                </div>
                <div class="card-body">
                    <p>{t('Forecast administration interface will be implemented here')}</p>
                </div>
            </div>
        {/if}

        <!-- AI Analyses management -->
        {#if activeTab === 'ai-analyses'}
            <div class="card">
                <div class="card-header">
                    <h5>{t('AI Analyses Management')}</h5>
                </div>
                <div class="card-body">
                    <p>{t('AI Analysis administration interface will be implemented here')}</p>
                </div>
            </div>
        {/if}

        <!-- OpenAI Models management -->
        {#if activeTab === 'openai-models'}
            <div class="card">
                <div class="card-header d-flex justify-content-between align-items-center">
                    <h5>OpenAI модели</h5>
                    <button class="btn btn-sm btn-primary" on:click={openAddModelForm}>
                        <i class="bi bi-plus-circle me-1"></i> {t('Add Model')}
                    </button>
                </div>
                <div class="card-body">
                    <table class="table">
                        <thead>
                        <tr>
                            <th>{t('ID')}</th>
                            <th>Название</th>
                            <th>{t('Is Active')}</th>
                            <th>Действия</th>
                        </tr>
                        </thead>
                        <tbody>
                        {#if openAIModels.length === 0}
                            <tr>
                                <td colspan="5" class="text-center">{t('No models found')}</td>
                            </tr>
                        {:else}
                            {#each openAIModels as model}
                                <tr>
                                    <td>{model.model_id}</td>
                                    <td>{model.name}</td>
                                    <td>
                      <span class="badge bg-{model.is_active ? 'success' : 'secondary'}">
                        {model.is_active ? t('Yes') : t('No')}
                      </span>
                                    </td>
                                    <td>
                                        <button
                                                class="btn btn-sm btn-outline-primary me-1"
                                                on:click={() => openEditModelForm(model.model_id)}
                                                title={t('Edit Model')}
                                        >
                                            <i class="bi bi-pencil"></i>
                                        </button>
                                        <button
                                                class="btn btn-sm btn-outline-danger"
                                                on:click={() => confirmDelete('model', model.model_id, model.name)}
                                                title={t('Delete Model')}
                                        >
                                            <i class="bi bi-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            {/each}
                        {/if}
                        </tbody>
                    </table>
                </div>
            </div>
        {/if}
    {/if}
</div>

<!-- User Form Modal -->
<UserForm
        bind:show={showUserForm}
        bind:userId={selectedUserId}
        on:refresh={loadData}
/>

<!-- OpenAI Model Form Modal -->
<OpenAIModelForm
        bind:show={showModelForm}
        bind:modelId={selectedModelId}
        on:refresh={loadData}
/>

<!-- Confirm Delete Dialog -->
<ConfirmDeleteDialog
        bind:show={showDeleteConfirm}
        bind:loading={deleteInProgress}
        title={t('Confirm Delete')}
        message={t('Are you sure you want to delete') + ` "${deleteItemName}"? ` + t('This action cannot be undone.')}
        on:confirm={handleDelete}
/>